import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Slider, Typography } from '@mui/material';
import ImageInput from 'components/common/form/imageInput';
import getCroppedImg from 'helpers/imageCropHelper';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { useSnackbar } from 'notistack';
import useIsTallEnough from 'hooks/useIsTallEnough';
import M from 'messages';
import classes from './styles';

const CROP_HEIGHT = 488; // Fixed crop height

function ImageCropWrapper({
  defaultValue,
  onChange,
  disabled,
  setDisableSave,
  errorStyle,
  type,
  allowResetToDefault = false,
  defaultImageValue = null,
  previewClassName = 'preview',
  uploadText = M.get('form.dropFileOrUpload'),
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [origWidth, setOrigWidth] = useState(1);
  const [natHeight, setNatHeight] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [isSmallImage, setToSmallImage] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1 / 3);
  const [cropSize, setCropSize] = useState({
    width: CROP_HEIGHT * (1 / 3),
    height: CROP_HEIGHT,
  });
  const imageInputRef = useRef(null);
  const cropContainerRef = useRef(null);
  const cropperRef = useRef(null);
  const [isContainerOk, setIsContainerOk] = useState(true);

  const isTallEnough = useIsTallEnough(810);
  const showHeader = useIsTallEnough(700);

  useEffect(() => {
    if (!isCropperOpen) return undefined;

    let timeoutId = null;

    const checkContainerHeight = () => {
      const el = document.querySelector('[data-testid="container"]');
      if (!(el instanceof Element)) {
        return;
      }

      const height = el.offsetHeight;
      const ok = height >= 486 && height <= 490;

      if (ok) {
        setIsContainerOk(true);
        clearTimeout(timeoutId); // cancel any pending delayed error
      } else {
        // Wait a bit before showing the error in case height corrects itself
        timeoutId = setTimeout(() => {
          setIsContainerOk(false);
        }, 500);
      }
    };

    checkContainerHeight();
    window.addEventListener('resize', checkContainerHeight);

    return () => {
      window.removeEventListener('resize', checkContainerHeight);
      clearTimeout(timeoutId);
    };
  }, [isCropperOpen]);
  // Attach a non-passive wheel listener for better control
  useEffect(() => {
    const el = cropContainerRef.current;
    let cleanup = () => {};
    if (el) {
      const handler = evt => {
        if (zoom <= minZoom && evt.deltaY > 0) {
          evt.preventDefault();
        }
      };
      el.addEventListener('wheel', handler, { passive: false });
      cleanup = () => el.removeEventListener('wheel', handler);
    }
    return cleanup;
  }, [zoom, minZoom]);

  const fileUploadInterceptor = file => {
    if (file) {
      setImageToCrop(URL.createObjectURL(file));
      setIsCropperOpen(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixelsIn) => {
    setCroppedAreaPixels(croppedAreaPixelsIn);
  };

  const handleCropImage = async () => {
    try {
      setIsCropperOpen(false);
      const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
      if (imageInputRef.current) {
        imageInputRef.current.defaultFileUpload(croppedImage);
      }
    } catch (error) {
      enqueueSnackbar('Failed to crop image.', { variant: 'error' });
      setIsCropperOpen(false);
    }
  };

  const handleCancelCrop = () => {
    setIsCropperOpen(false);
    setImageToCrop(null);
    setIsContainerOk(true);
  };

  const handleAspectChange = newAspect => {
    setAspectRatio(newAspect);
    setCropSize({
      width: CROP_HEIGHT * newAspect,
      height: CROP_HEIGHT,
    });
  };

  const handleMediaLoaded = mediaSize => {
    const { naturalWidth, naturalHeight, width } = mediaSize;
    // 1) calculate the minimum zoom so that the image's displayed height is at least 488px
    const baseMinZoom = Math.max(1, CROP_HEIGHT / naturalHeight);

    // 2) (optional) bump it if the wine is very narrow
    const wineAspectRatio = naturalWidth / naturalHeight;
    const initialZoom =
      wineAspectRatio < 0.5
        ? baseMinZoom * 1 // 30% boost on narrow bottles
        : baseMinZoom;

    if (naturalHeight < CROP_HEIGHT) {
      setToSmallImage(true);
    } else {
      setToSmallImage(false);
    }
    setNatHeight(naturalHeight);
    setOrigWidth(width);
    setMinZoom(baseMinZoom);
    setZoom(initialZoom);
    setCrop({ x: 0, y: 0 }); // center it
  };

  return (
    <>
      <ImageInput
        onChange={onChange}
        disabled={disabled}
        defaultValue={defaultValue}
        allowResetToDefault={allowResetToDefault}
        defaultImageValue={defaultImageValue}
        setDisableSave={setDisableSave}
        errorStyle={errorStyle}
        type={type}
        ref={imageInputRef}
        onDrop={acceptedFiles => {
          fileUploadInterceptor(acceptedFiles[0]);
        }}
        previewClassName={previewClassName}
        uploadText={uploadText}
      />

      <Dialog
        open={isCropperOpen}
        onClose={handleCancelCrop}
        maxWidth="md"
        fullWidth
        fullScreen={!isTallEnough}
        TransitionProps={{
          onEntered: () => window.dispatchEvent(new Event('resize')), // ensure proper crop layout
        }}>
        {showHeader && <DialogTitle>Crop Image</DialogTitle>}
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingBottom: '0px',
            ...(!showHeader && { paddingTop: '0px' }),
          }}>
          <Box
            ref={cropContainerRef}
            sx={{
              position: 'relative',
              width: '100%',
              height: '488px',
              background: '#333',
            }}>
            {imageToCrop && (
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                showGrid
                restrictPosition
                minZoom={minZoom}
                aspect={aspectRatio}
                cropSize={cropSize}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onMediaLoaded={handleMediaLoaded}
                maxZoom={5}
                setCropperRef={el => {
                  cropperRef.current = el; // el must be the actual DOM element
                }}
              />
            )}
          </Box>
          {!isContainerOk ? (
            <Typography color="error" sx={{ mt: 1 }}>
              {M.get('imageCropper.errorLooksScreenZoomIn')}
            </Typography>
          ) : origWidth * zoom < cropSize.width ? (
            <Typography color="error" sx={{ mt: 1 }}>
              {M.get('imageCropper.errorZoomORChangeAcceptRation')}
            </Typography>
          ) : isSmallImage ? (
            <Typography color="warning.main" sx={{ mt: 1 }}>
              {M.get('imageCropper.warningToSmallImage')}
            </Typography>
          ) : zoom > natHeight / CROP_HEIGHT ? (
            <Typography color="warning.main" sx={{ mt: 1 }}>
              {M.get('imageCropper.warningSoMuchZoom')}
            </Typography>
          ) : (
            <Typography color="warning.main" sx={{ mt: 1 }}>
              &nbsp;
            </Typography>
          )}
          <Box sx={classes.controls}>
            <Box sx={classes.sliderContainer}>
              <Typography variant="overline" sx={classes.sliderLabel}>
                Zoom
              </Typography>
              <Slider
                sx={classes.slider}
                value={zoom}
                min={minZoom}
                max={5}
                step={0.1}
                onChange={(e, newZoom) => setZoom(newZoom)}
              />
            </Box>

            <Box sx={classes.buttonGroup}>
              <Typography variant="overline" sx={classes.sliderLabel}>
                Aspect Ratio
              </Typography>
              <Button
                sx={{
                  ...classes.button,
                  ...(aspectRatio === 1 / 3 && classes.selectedButton),
                }}
                onClick={() => handleAspectChange(1 / 3)}>
                1:3
              </Button>
              <Button
                sx={{
                  ...classes.button,
                  ...(aspectRatio === 1 / 4 && classes.selectedButton),
                }}
                onClick={() => handleAspectChange(1 / 4)}>
                1:4
              </Button>
              <Button
                sx={{
                  ...classes.button,
                  ...(aspectRatio === 1 / 4.5 && classes.selectedButton),
                }}
                onClick={() => handleAspectChange(1 / 4.5)}>
                1:4.5
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelCrop}>Cancel</Button>
          <Button
            onClick={handleCropImage}
            color="primary"
            disabled={origWidth * zoom < cropSize.width || !isContainerOk}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ImageCropWrapper.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  setDisableSave: PropTypes.func,
  type: PropTypes.string,
  errorStyle: PropTypes.bool,
  allowResetToDefault: PropTypes.bool,
  defaultImageValue: PropTypes.string,
  previewClassName: PropTypes.string,
  uploadText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export default ImageCropWrapper;
